import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import { cn } from '@techsofit/ui';

export interface ActionItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  danger?: boolean;
}

const MENU_WIDTH = 208; // w-52

/**
 * A "more actions" (⋮) menu for a row or header. The dropdown is rendered in a portal to
 * <body> with fixed positioning, so it never gets clipped by an `overflow-hidden` table/card,
 * and flips above the trigger when there isn't room below. Closes on outside-click / Escape /
 * scroll, and stops click propagation so it never triggers the row it sits on.
 */
export function ActionMenu({ items, label = 'More actions' }: { items: ActionItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  function place() {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    const estHeight = items.length * 42 + 8;
    const spaceBelow = window.innerHeight - r.bottom;
    const top = spaceBelow < estHeight + 8 && r.top > estHeight ? r.top - estHeight - 4 : r.bottom + 4;
    const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
    setPos({ top, left });
  }

  useLayoutEffect(() => {
    if (open) place();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const tgt = e.target as Node;
      if (triggerRef.current?.contains(tgt) || menuRef.current?.contains(tgt)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="inline-flex size-9 items-center justify-center rounded-control text-muted transition-colors hover:bg-surface hover:text-ink"
      >
        <MoreVertical className="size-5" aria-hidden />
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="z-50 overflow-hidden rounded-card border border-line bg-card p-1 shadow-pop animate-pop-in"
          >
            {items.map((item, i) => (
              <button
                key={i}
                role="menuitem"
                type="button"
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-control px-3 py-2.5 text-left text-sm font-medium transition-colors',
                  item.danger ? 'text-bad-ink hover:bg-bad-soft' : 'text-ink-soft hover:bg-surface hover:text-ink',
                )}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
